import React, { useState } from 'react';
import { testPasswordReset, checkAuthState, handleAuthCallback } from '../utils/testPasswordReset';
import { testSupabaseConnectivity, testPasswordResetWithDifferentUrls, checkEnvironmentConfig } from '../utils/networkDiagnostics';
import toast from 'react-hot-toast';

const DebugAuth = () => {
  const [email, setEmail] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);

  const handleTestReset = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    console.log('Testing password reset for:', email);
    
    const result = await testPasswordReset(email);
    setResults(prev => ({
      ...prev,
      passwordReset: result
    }));
    
    if (result.success) {
      toast.success('Password reset email should be sent!');
    } else {
      toast.error('Password reset failed');
    }
    
    setLoading(false);
  };

  const handleCheckAuth = async () => {
    const result = await checkAuthState();
    setResults(prev => ({
      ...prev,
      authState: result
    }));
  };

  const handleTestCallback = async () => {
    const result = await handleAuthCallback();
    setResults(prev => ({
      ...prev,
      authCallback: result
    }));
    
    if (result.success) {
      toast.success('Auth callback successful!');
    } else {
      toast.error('Auth callback failed');
    }
  };

  const handleNetworkDiagnostics = async () => {
    setLoading(true);
    toast.loading('Running network diagnostics...');
    
    const result = await testSupabaseConnectivity();
    setResults(prev => ({
      ...prev,
      networkDiagnostics: result
    }));
    
    toast.dismiss();
    toast.success('Network diagnostics completed');
    setLoading(false);
  };

  const handleTestMultipleUrls = async () => {
    if (!email) {
      toast.error('Please enter an email address first');
      return;
    }

    setLoading(true);
    toast.loading('Testing different redirect URLs...');
    
    const result = await testPasswordResetWithDifferentUrls(email);
    setResults(prev => ({
      ...prev,
      multipleUrlTest: result
    }));
    
    toast.dismiss();
    toast.success('URL tests completed');
    setLoading(false);
  };

  const handleCheckConfig = () => {
    const config = checkEnvironmentConfig();
    setResults(prev => ({
      ...prev,
      environmentConfig: config
    }));
    toast.success('Environment configuration checked');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Auth Debug Tools
        </h1>
        
        {/* Test Password Reset */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Test Password Reset
          </h2>
          
          <div className="flex gap-4 mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email to test"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleTestReset}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Testing...' : 'Test Reset'}
            </button>
          </div>
          
          {results.passwordReset && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Result:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.passwordReset, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Check Auth State */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Check Current Auth State
          </h2>
          
          <button
            onClick={handleCheckAuth}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 mb-4"
          >
            Check Auth State
          </button>
          
          {results.authState && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Auth State:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.authState, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Test Auth Callback */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Test Auth Callback (if URL has tokens)
          </h2>
          
          <button
            onClick={handleTestCallback}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 mb-4"
          >
            Test Auth Callback
          </button>
          
          {results.authCallback && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Callback Result:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.authCallback, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Network Diagnostics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Network Diagnostics
          </h2>
          
          <button
            onClick={handleNetworkDiagnostics}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 mb-4 disabled:opacity-50"
          >
            Run Network Tests
          </button>
          
          {results.networkDiagnostics && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Network Test Results:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.networkDiagnostics, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Test Multiple URLs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Test Multiple Redirect URLs
          </h2>
          
          <button
            onClick={handleTestMultipleUrls}
            disabled={loading || !email}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 mb-4 disabled:opacity-50"
          >
            Test All URL Variants
          </button>
          
          {results.multipleUrlTest && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Multiple URL Test Results:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.multipleUrlTest, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Environment Config */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Environment Configuration
          </h2>
          
          <button
            onClick={handleCheckConfig}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 mb-4"
          >
            Check Config
          </button>
          
          {results.environmentConfig && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Environment Config:</h3>
              <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-auto">
                {JSON.stringify(results.environmentConfig, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Current URL Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Current URL Info
          </h2>
          
          <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <div><strong>Origin:</strong> {window.location.origin}</div>
            <div><strong>Pathname:</strong> {window.location.pathname}</div>
            <div><strong>Search:</strong> {window.location.search}</div>
            <div><strong>Hash:</strong> {window.location.hash}</div>
          </div>
          
          {window.location.hash && (
            <div className="mt-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Hash Parameters:</h3>
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {Array.from(new URLSearchParams(window.location.hash.substring(1))).map(([key, value]) => (
                  <div key={key}>
                    <strong>{key}:</strong> {key.includes('token') ? '***HIDDEN***' : value}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugAuth;
